import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Extra behaviour for the cart drawer:
 * - "Want another variant?" row under each cart line
 * - "Add more" product carousel
 * Both add a variant to the cart and let the existing cart components refresh the drawer.
 */

/**
 * @param {HTMLElement} source - element the cart event is dispatched from
 * @param {string} variantId
 */
async function addVariantToCart(source, variantId) {
  const cartItems = document.querySelectorAll('cart-items-component');
  const sectionIds = [];
  for (const item of cartItems) {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    }).then((r) => r.json());

    if (response.status) {
      source.dispatchEvent(
        new CartErrorEvent({
          error: response.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        })
      );
      deferred.reject(new Error(response.message || 'Add to cart failed'));
      return { ok: false, message: response.description || response.message };
    }

    const cart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'le-cart-extras',
        itemCount: 1,
        sections: response.sections,
        didError: false,
      },
    });
    return { ok: true };
  } catch (error) {
    deferred.reject(error);
    return { ok: false, message: error?.message };
  }
}

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // Add button next to a variant select
  const addButton = target.closest('[data-le-add]');
  if (addButton instanceof HTMLButtonElement) {
    const wrapper = addButton.closest('[data-le-variant-picker]');
    const select = wrapper?.querySelector('select');
    if (!(select instanceof HTMLSelectElement) || !select.value) return;
    if (addButton.disabled) return;

    const label = addButton.textContent;
    addButton.disabled = true;
    addButton.dataset.loading = 'true';

    const result = await addVariantToCart(addButton, select.value);

    if (!result.ok) {
      addButton.disabled = false;
      delete addButton.dataset.loading;
      addButton.textContent = label;
      const error = wrapper?.querySelector('[data-le-error]');
      if (error) {
        error.textContent = result.message || '';
        setTimeout(() => (error.textContent = ''), 6000);
      }
    }
    return;
  }

  // Carousel arrows
  const arrow = target.closest('[data-le-arrow]');
  if (arrow instanceof HTMLElement) {
    const track = arrow.closest('[data-le-carousel]')?.querySelector('[data-le-track]');
    if (!(track instanceof HTMLElement)) return;
    const direction = arrow.dataset.leArrow === 'prev' ? -1 : 1;
    track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
  }
});

// Keep the price in the "add more" card in sync with the selected variant
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.closest('[data-le-variant-picker]')) return;
  const price = select.selectedOptions[0]?.dataset.price;
  const priceEl = select.closest('[data-le-card]')?.querySelector('[data-le-price]');
  if (price && priceEl) priceEl.textContent = price;
});
